/**
 * Under Minimum (owner, 22 Oct 2026): the third payroll rule. When it is ON,
 * every new sale whose cart minimum is above what was paid takes the gap off
 * the rep's commission on that sale, as one paycheck line per sale.
 *
 * Every name the backend contract uses lives here, so a rename on the
 * backend is a one-file change on the frontend.
 */

/** Company / payroll-settings fields (GET company, PATCH company, history). */
export const UNDER_MINIMUM_FIELDS = {
  method: 'underMinimumMethod',
  toggle: 'isUnderMinimumEnabled',
  enabledAt: 'underMinimumEnabledAt',
  threshold: 'underMinimumThreshold',
  methodValue: 'underMinimumMethodValue',
} as const;

/** How much an Under Minimum sale costs the rep (owner, 22 Oct 2026). */
export type UnderMinimumMethod =
  | 'loseAll'
  | 'flatFee'
  | 'payBackGap'
  | 'losePercent'
  | 'lowerRatePoints';

export type UnderMinimumBoxKind = 'points' | 'percent' | 'dollars';

export const UNDER_MINIMUM_METHODS: {
  help: string;
  label: string;
  value: UnderMinimumMethod;
  // The number box the method needs, if any.
  box?: { label: string; kind: UnderMinimumBoxKind };
}[] = [
  {
    value: 'payBackGap',
    label: 'Pay back the gap',
    help: 'The rep pays back the full gap between the cart minimum and what was paid.',
  },
  {
    value: 'loseAll',
    label: 'Lose the whole commission on the sale',
    help: 'The rep loses their whole commission on that sale.',
  },
  {
    value: 'losePercent',
    label: 'Lose a % of the commission',
    box: { kind: 'percent', label: '% of the commission' },
    help: 'The rep loses this % of their commission on that sale.',
  },
  {
    value: 'flatFee',
    label: 'Flat $ per under-minimum sale',
    box: { kind: 'dollars', label: '$ per under-minimum sale' },
    help: 'The rep pays this flat amount for each under-minimum sale.',
  },
  {
    // Owner, 22 Oct 2026 ~20:10Z: fifth option.
    value: 'lowerRatePoints',
    label: 'Lower the commission rate by X points',
    box: { kind: 'points', label: 'Points off the commission rate' },
    help: "The rep's commission rate on that sale drops by this many points (e.g. 30% → 25%).",
  },
];

export const getUnderMinimumMethod = (value?: string | null) =>
  UNDER_MINIMUM_METHODS.find((method) => method.value === value) ??
  UNDER_MINIMUM_METHODS[0];

/** Defaults when the company has never saved the rule. */
export const UNDER_MINIMUM_DEFAULTS = {
  threshold: 1,
  method: 'payBackGap' as UnderMinimumMethod,
} as const;

/** `payrollRule` the backend puts on each Under Minimum paycheck line. */
export const UNDER_MINIMUM_RULE = 'underMinimum' as const;

/** The paycheck/payout label (and the line's note). */
export const UNDER_MINIMUM_LABEL = 'Under Minimum';

/** Item key that carries the payroll stamp's meta on a paycheck line. */
export const UNDER_MINIMUM_META_KEY = 'payrollMeta' as const;

/** The stamp meta on each Under Minimum line (backend contract, 22 Oct). */
export interface UnderMinimumMeta {
  isVoided?: boolean;
  // Only on lines retired with nothing left: never shown as a sub-row.
  retiredBy?: unknown;
  isRefunded?: boolean;
  // Current values, after refunds: what the sub-row shows.
  gap?: number | string;
  target?: number | string;
  // At the sale.
  saleGap?: number | string;
  cartPaid?: number | string;
  cartMinimum?: number | string;
  alreadyPaid?: number | string;
  saleCartPaid?: number | string;
  sharePercent?: number | string;
  approvedByName?: string | null;
  methodUsed?: UnderMinimumMethod;
  thresholdUsed?: number | string;
  commissionCap?: number | string;
  approvedByUserId?: number | null;
  saleCartMinimum?: number | string;
  underMinimumSaleId?: number | null;
  checkoutId?: number | string | null;
  methodValueUsed?: number | string | null;
}

interface UnderMinimumLine {
  id?: number;
  notes?: string;
  payrollRule?: string;
  amount?: number | string;
  checkoutId?: number | string;
  payrollMeta?: UnderMinimumMeta | null;
}

export const isUnderMinimumItem = (item?: { payrollRule?: string }) =>
  item?.payrollRule === UNDER_MINIMUM_RULE;

export const getUnderMinimumMeta = (item: UnderMinimumLine) =>
  (item[UNDER_MINIMUM_META_KEY] ?? {}) as UnderMinimumMeta;

const NBSP = '\u00a0';

const toNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

/**
 * "Sale #42307 · paid $140.00 · min $145.00". Parts the line does not carry
 * are left out; with no meta at all it falls back to the line's note.
 */
export const describeUnderMinimumSale = (
  item: UnderMinimumLine,
  format: (value: number) => string
) => {
  const meta = getUnderMinimumMeta(item);
  const checkoutId = meta.checkoutId ?? item.checkoutId;
  const paid = toNumber(meta.cartPaid);
  const minimum = toNumber(meta.cartMinimum);

  // Each part keeps its words together (no-break space), so a narrow
  // screen wraps between parts, never inside "min $145.00".
  const parts = [
    checkoutId !== undefined && checkoutId !== null && checkoutId !== ''
      ? `Sale${NBSP}#${checkoutId}`
      : undefined,
    paid !== undefined ? `paid${NBSP}${format(paid)}` : undefined,
    minimum !== undefined ? `min${NBSP}${format(minimum)}` : undefined,
  ].filter(Boolean);

  return parts.length
    ? parts.join(' · ')
    : item.notes?.trim() || UNDER_MINIMUM_LABEL;
};

/**
 * Lines that get a sub-row: not retired, and not $0. They still count in
 * the backend's underMinimumTotal either way.
 */
export const isShownUnderMinimumLine = (item: UnderMinimumLine) => {
  const meta = getUnderMinimumMeta(item);
  return (
    !meta.retiredBy && Math.round(Math.abs(Number(item.amount) || 0) * 100) > 0
  );
};

/** Sale order (oldest sale first), then line id. */
export const sortUnderMinimumLines = <T extends UnderMinimumLine>(items: T[]) =>
  [...items].sort((a, b) => {
    const left = Number(getUnderMinimumMeta(a).checkoutId ?? a.checkoutId);
    const right = Number(getUnderMinimumMeta(b).checkoutId ?? b.checkoutId);
    if (Number.isFinite(left) && Number.isFinite(right) && left !== right) {
      return left - right;
    }
    return (a.id ?? 0) - (b.id ?? 0);
  });

/**
 * The PIN rule for the cart (owner, 22 Oct): approval is needed when the cart
 * minimum minus what the cart charges is at least the company threshold.
 * Gaps under the threshold (default $1) sell without a PIN. Compared in cents.
 */
export const isGapAtLeastThreshold = (
  minimum: number,
  paid: number,
  threshold?: number | string | null
) => {
  const gapCents = Math.round((minimum - paid) * 100);
  const parsed = toNumber(threshold);
  const thresholdCents = Math.round(
    (parsed === undefined || parsed < 0
      ? UNDER_MINIMUM_DEFAULTS.threshold
      : parsed) * 100
  );
  return gapCents > 0 && gapCents >= thresholdCents;
};
